import { Grid, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import {
	DefaultValues,
	FormProvider,
	SubmitHandler,
	useForm,
} from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as Yup from 'yup';
import { FormType } from '@/api/enum/formType.enum';
import {
	Dispatch,
	forwardRef,
	SetStateAction,
	useEffect,
	useImperativeHandle,
} from 'react';
import AppDatePicker from '@/common/components/form-inputs/AppDatePicker';
import dayjs from 'dayjs';
import { API_DATE_FORMAT, notFutureDate } from '@/utils/dateHelper';
import AppDropDownMenu from '@/common/components/form-inputs/AppDropDownMenu';
import { SEX_LIST } from '@/common/constants/common';
import AppTextField from '@/common/components/form-inputs/AppTextField';
import AppRadioGroup from '@/common/components/form-inputs/AppRadioGroup';
import { useGetDicListQuery, useGetStaffAllListQuery } from '@/api/common';
import { useSelector } from 'react-redux';
import { selectMyInfo } from '@/pages/auth/authSlice';

interface Props {
	type: FormType;
	details?: any;
	setType: Dispatch<SetStateAction<FormType>>;
}

const EditLatestFaceSheetInformationForm = forwardRef(
	({ type, setType, details }: Props, ref) => {
		const { t } = useTranslation();
		const defaultValues: DefaultValues<any> = {};

		const userInfo = useSelector(selectMyInfo);

		const { data: staffList }: any = useGetStaffAllListQuery(
			userInfo?.currentBranch?.id || '',
			{
				refetchOnMountOrArgChange: true,
				skip: !userInfo?.currentBranch,
			}
		);

		useEffect(() => {
			if (details) methods.reset(details);
		}, [details]);

		const formSchema = Yup.object().shape({
			latestFaceSheetDate: Yup.string()
				.nullable()
				.test(notFutureDate(t('errorMessage.dateCannotBeFuture'))),
		});
		const methods = useForm<any>({
			defaultValues,
			resolver: yupResolver(formSchema),
			mode: 'onChange',
			shouldFocusError: true,
		});

		useImperativeHandle(ref, () => ({
			getFormData: () => methods.getValues(),
			validateForm: () => {
				methods.trigger();
				methods.formState.isValid;
				return methods.trigger().then(() => methods.formState.isValid);
			},
		}));

		return (
			<>
				<FormProvider {...methods}>
					<Typography variant='h6' sx={{ fontWeight: 'bold' }} marginBottom={2}>
						{t('preAdmission.latestFaceSheetInformation')}
					</Typography>
					<form onSubmit={methods.handleSubmit((e) => e.preventDefault())}>
						<Grid container spacing={3} marginTop={0} marginBottom={3}>
							<Grid size={6}>
								<AppDropDownMenu
									optional={true}
									control={methods.control}
									name='latestFaceSheetWorker'
									label={t('preAdmission.name')}
									options={staffList?.data || []}
								/>
							</Grid>
							<Grid size={6}>
								<AppDatePicker
									maximumDate={dayjs()
										.startOf('day')
										.format(API_DATE_FORMAT)}
									control={methods.control}
									name='latestFaceSheetDate'
									label={t('preAdmission.updatedDate')}
								/>
							</Grid>
						</Grid>
					</form>
				</FormProvider>
			</>
		);
	}
);

EditLatestFaceSheetInformationForm.displayName =
	'EditLatestFaceSheetInformationForm';

export default EditLatestFaceSheetInformationForm;
